import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {head, isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useInactivateCashRegisterMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {DmsDataGrid, catchUnhandledDataGridActions, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['name'] as const;

export function CashRegisters() {
  const navigate = useNavigate();
  const [inactivateCashRegister] = useInactivateCashRegisterMutation();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    refreshData,
    getColumnData,
  }) => {
    const id = isArray(rowId) ? head(rowId) : rowId;

    if (isNil(id)) {
      return;
    }

    const name = getColumnData<string>('name')?.value;

    match(actionKey)
      .with('DETAIL', 'EDIT', () =>
        navigate(composePath(settingsRoutes.cashRegistersDetail, {params: {id}}))
      )
      .with('DELETE', () =>
        openDeleteDialog({
          text: `${i18n.t('general.actions.delete')} ${name ?? ''}`,
          onConfirm: () =>
            inactivateCashRegister({cashRegisterId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const title = i18n.t('page.accounting.labels.cashRegisters');

  const header: SettingsTemplateHeader = {
    title,
    actions: [
      {
        type: 'button',
        title: i18n.t('page.accountingSettings.labels.newCashRegister'),
        onClick: () => navigate(settingsRoutes.newCashRegister),
        'data-testid': testIds.settings.cashRegisters('createNew'),
      },
    ],
  };

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate header={header} data-testid="cashregister-settings-modal">
        <DmsDataGrid
          gridCode="accounting-cash-register"
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          data-testid={testIds.settings.cashRegisters('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
